import { ElMessage } from "element-plus";
import { http } from "@shared/utils/request";
import { t } from "@shared/utils/i18n";

export type LegacyJumpResult = {
    jumpUrl: string;
    midToken?: string;
    mid_token?: string;
};

/** 与老系统 midAuth.js 约定的交接键 */
export const MID_TOKEN_STORAGE_KEY = "mid_token";
const MID_TOKEN_WINDOW_PREFIX = "mid_token:";
const MID_1P_RETURN_WINDOW_PREFIX = "1p_return:";

/** 获取跳转老系统广告页的 URL + midToken */
export function fetchAdsJumpUrl(): Promise<LegacyJumpResult> {
    return http.post<LegacyJumpResult>("/customer/ads/jumpUrl", {});
}

/** 获取跳转老系统促销页的 URL + midToken */
export function fetchActivityJumpUrl(): Promise<LegacyJumpResult> {
    return http.post<LegacyJumpResult>("/customer/activity/jumpUrl", {});
}

function pickMidToken(result?: LegacyJumpResult | null): string {
    return String(result?.midToken || result?.mid_token || "").trim();
}

/** 促销菜单跳转老系统时落到 BD 活动列表 */
export const LEGACY_ACTIVITY_LANDING_PATH = "/promote/promotionsauditNew";

/**
 * 本地调试：跳老系统 9527 时改为 "http://localhost:9527"。
 * 空字符串 = 走接口返回的 jumpUrl（发版保持空）。
 */
const LOCAL_LEGACY_ORIGIN = "";
// const LOCAL_LEGACY_ORIGIN = "http://localhost:9527";

function getLegacyOrigin(): string {
    return String(LOCAL_LEGACY_ORIGIN || "").replace(/\/$/, "");
}

/** 有 LOCAL_LEGACY_ORIGIN 则替换域名；否则用接口 jumpUrl。pathOverride 只换路径。 */
export function resolveLegacyJumpUrl(rawJumpUrl: string, pathOverride?: string): string {
    const raw = String(rawJumpUrl || "").trim();
    const localOrigin = getLegacyOrigin();
    try {
        const url = new URL(raw || localOrigin || window.location.href, window.location.origin);
        if (pathOverride) {
            url.pathname = pathOverride;
            url.search = "";
            url.hash = "";
        }
        const path = `${url.pathname}${url.search}${url.hash}`;
        if (localOrigin) return `${localOrigin}${path}`;
        if (!raw) return "";
        return `${url.origin}${path}`;
    } catch {
        if (localOrigin) return `${localOrigin}${pathOverride || "/"}`;
        return raw;
    }
}

function persistMidToken(midToken: string, win?: Window | null): void {
    const fromUrl = window.location.href;
    if (midToken) {
        try {
            localStorage.setItem(MID_TOKEN_STORAGE_KEY, midToken);
        } catch {
            // ignore quota / private mode
        }
    }
    try {
        const tokenPart = midToken ? `${MID_TOKEN_WINDOW_PREFIX}${midToken}` : "";
        const returnPart = `${MID_1P_RETURN_WINDOW_PREFIX}${encodeURIComponent(fromUrl)}`;
        (win || window).name = tokenPart ? `${tokenPart}|${returnPart}` : returnPart;
    } catch {
        // ignore closed / restricted window
    }
}

export function applyLegacyJump(result: LegacyJumpResult, existingWin?: Window | null, pathOverride?: string): void {
    const jumpUrl = resolveLegacyJumpUrl(result?.jumpUrl, pathOverride);
    if (!jumpUrl) {
        existingWin?.close();
        throw new Error("empty jumpUrl");
    }
    const midToken = pickMidToken(result);
    const win = existingWin ?? window.open("about:blank", "_blank");
    persistMidToken(midToken, win || undefined);
    if (!win) {
        window.location.href = jumpUrl;
        return;
    }
    win.location.href = jumpUrl;
}

/**
 * 先同步打开空白页再换真实 URL，避免 await 后弹窗被拦。
 * 必须在用户点击的同步调用栈里执行（如侧栏点击 → 路由守卫）。
 */
function openLegacyInNewTab(fetchResult: () => Promise<LegacyJumpResult>, pathOverride?: string): void {
    const win = window.open("about:blank", "_blank");
    if (!win) {
        ElMessage.warning(t("请允许浏览器弹出窗口"));
        return;
    }
    void (async () => {
        try {
            const result = await fetchResult();
            applyLegacyJump(result, win, pathOverride);
        } catch {
            win.close();
            ElMessage.error(t("打开老系统失败"));
        }
    })();
}

/** 新窗口打开老系统广告页 */
export function openLegacyAds(): void {
    openLegacyInNewTab(fetchAdsJumpUrl);
}

/** 新窗口打开老系统促销页（落到 BD 活动列表） */
export function openLegacyActivity(): void {
    openLegacyInNewTab(fetchActivityJumpUrl, LEGACY_ACTIVITY_LANDING_PATH);
}
